import {Button, Grid} from "@mui/material";
import "dayjs/locale/it";
import {RefObject, useContext, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState, useRecoilValue} from "recoil";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";
import {useCommercialCategoryRepository} from "../../../../api/repositories/CommercialCategoryRepository";
import {usePeakRepository} from "../../../../api/repositories/PeakRepository";
import {CommercialCategorySelectFE} from "../../../../api/types/commercialCategory";
import {SelectElementModelBE, SelectElementModelFE} from "../../../../api/types/generic";
import {FormPeakModel, PeakEntityRequest, PeakEntityResponse,} from "../../../../api/types/peak";
import CustomBanner from "../../../../common/components/CustomBanner";
import {FormAutocomplete, FormDateTimePicker, FormTextField,} from "../../../../common/form-fields";
import {commercialSegmentListAtom} from "../../../../state/commercialSegmentsAtom";
import {peakCreationAtom} from "../../../../state/peaksRecoil";
import {
    fromGenericBeToGenericFe,
    fromSingleGenericBeToSingleGenericFe,
    fromSingleGenericFeToSingleGenericBe,
} from "../../../../utils";
import {BASE_DEFINITION_FORM_STEPS, SET_SNACKBAR} from "../../../../constants";
import DealerAndTenant from "../../../../common/components/DealerAndTenant";

type PeakBaseDefinitionProps = { submitRef: RefObject<HTMLButtonElement>, refresh: Function };

const PeakBaseDefinition = ({submitRef, refresh}: PeakBaseDefinitionProps) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    // Use Form Context
    const {register, setValue, handleSubmit, watch, getValues} = useFormContext<FormPeakModel>();

    // Use Repository Hooks
    const {putPeakList, postPeakList} = usePeakRepository();

    // Use Repository Hooks
    const {getCommercialCategoriesByCommercialSegmentId} = useCommercialCategoryRepository();

    // Use Recoil
    const commercialSegmentList = useRecoilValue(commercialSegmentListAtom);
    const [peakCreation, setPeakCreation] = useRecoilState(peakCreationAtom);
    const {readOnly, definitionData, inUse} = peakCreation;

    useEffect(() => {
        setValue("id", definitionData?.id as number)
        setValue("name", definitionData?.name as string)
        setValue("description", definitionData?.description as string)
        setValue("commercialSegmentId", definitionData?.commercialSegmentId as SelectElementModelFE)
        setValue("commercialCategory", definitionData?.commercialCategory as CommercialCategorySelectFE)
        setValue("from", definitionData?.from as string)
        setValue("to", definitionData?.to as string)
        setValue("used", definitionData?.used as boolean)
        setValue("legalEntity", definitionData?.legalEntity ? {
            code: definitionData?.legalEntity?.code as string,
            label: t('translation:legalEntities.'.concat(definitionData?.legalEntity?.code as string))
        } : null)
        setValue("dealer", definitionData?.dealer ? {
            code: definitionData?.dealer?.code as string,
            label: t('translation:dealers.'.concat(definitionData?.dealer?.code as string))
        } : null)
        getCategoryList(definitionData?.commercialSegmentId as SelectElementModelFE)
    }, [])

    // Use State
    const [commercialCategoryList, setCommercialCategoryList] = useState<SelectElementModelFE[]>([]);
    const [commercialCategoryListOriginal, setCommercialCategoryListOriginal] = useState<CommercialCategorySelectFE[]>([]);

    // Watch della legal entity per triggerare il filtraggio
    const watchedLegalEntity = watch("legalEntity");

    const getCategoryList = async (value: SelectElementModelFE) => {
        const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((value as SelectElementModelFE)?.code as string);
        const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
        setCommercialCategoryListOriginal(commercialCategoryList);
    };

    // Effect per filtrare le categorie quando cambia la legal entity
    useEffect(() => {
        const legalEntityCode = (watchedLegalEntity?.code as string) 
            ?? (user.legalEntities?.length === 1 ? user.legalEntities[0].name : null);
        
        if (!legalEntityCode || commercialCategoryListOriginal.length === 0) {
            setCommercialCategoryList([]);
            return;
        }

        // Espandi le categorie in base alla legal entity selezionata
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        commercialCategoryListOriginal.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (labelObj.country === legalEntityCode) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });
        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [watchedLegalEntity, commercialCategoryListOriginal, user.legalEntities]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        setValue("commercialSegmentId", value);
        setValue("commercialCategory", null);
        
        if (value) {
            const commercialCategoriesData = await getCommercialCategoriesByCommercialSegmentId((value as SelectElementModelFE).code as string);
            const commercialCategoryList: CommercialCategorySelectFE[] = fromGenericBeToGenericFe(commercialCategoriesData);
            setCommercialCategoryListOriginal(commercialCategoryList);
        } else {
            setCommercialCategoryListOriginal([]);
        }
    };

    const getDefinitionData = (data: PeakEntityResponse) => ({
        ...data,
        commercialCategory: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory
        ) as CommercialCategorySelectFE,
        commercialSegmentId: fromSingleGenericBeToSingleGenericFe(
            data.commercialCategory.commercialSegment
        ) as SelectElementModelFE,
        dealer: {code: data.dealer, label: data.dealer},
        legalEntity: {code: data.legalEntity, label: data.legalEntity},
    });

    const confirmDefinition = async (values: FormPeakModel) => {
        const newPeakList: PeakEntityRequest = {
            commercialCategory: fromSingleGenericFeToSingleGenericBe(values.commercialCategory as SelectElementModelFE),
            name: values.name,
            from: values.from,
            to: values.to,
            description: values.description,
            used: inUse,
            dealer: dealerFilteredOptions.length === 1 ? dealerFilteredOptions[0] : values.dealer?.code as string,
            legalEntity: legalEntityOptions.length === 1 ? everyLegalEntitiesWithoutAll[0] : values.legalEntity?.code as string,
        };

        try {
            const creationResponse = definitionData?.id
                ? await putPeakList({
                    id: definitionData?.id,
                    ...JSON.parse(JSON.stringify(newPeakList))
                })
                : await postPeakList(newPeakList);

            setPeakCreation({
                ...peakCreation,
                peakId: creationResponse.id,
                step: BASE_DEFINITION_FORM_STEPS.ASSOCIATION,
                completed: [true, ...peakCreation.completed.slice(1)],
                definitionData: getDefinitionData(creationResponse)
            });
            refresh()
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    return (
        <Grid container spacing={2} alignItems="start">
            {inUse && <CustomBanner message={t("pricing:peaks.banner.definition")} severity={"warning"}/>}
            <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                {/* NAME PEAK */}
                <Grid item xs={12} md={3}>
                    <FormTextField
                        {...register("name")}
                        label={`${t("pricing:peaks.form.labels.name")}*`}
                        readOnly={readOnly || inUse}
                        autoFocus
                    />
                </Grid>
                {/* DESCRIPTION PEAK LIST */}
                <Grid item xs={12} md={3}>
                    <FormTextField
                        {...register("description")}
                        label={`${t("pricing:peaks.form.labels.description")}*`}
                        readOnly={readOnly || inUse}
                    />
                </Grid>
                {/* COMMERCIAL SEGMENTS */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("commercialSegmentId")}
                        label={`${t("pricing:peaks.form.labels.commercialSegment")}*`}
                        options={commercialSegmentList}
                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                        readOnly={readOnly || inUse}
                        handleChange={handleChangeCommercialSegment}
                    />
                </Grid>
                {/* COMMERCIAL CATEGORY */}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("commercialCategory")}
                        label={`${t("pricing:peaks.form.labels.commercialCategory")}*`}
                        options={commercialCategoryList}
                        isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                        readOnly={readOnly || inUse || Boolean(!watch("commercialSegmentId"))}
                    />
                </Grid>
                {/* RANGE PEAK LIST */}
                {/* FROM */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("from")}
                        label={`${t("pricing:peaks.form.labels.startDateValidity")}*`}
                        readOnly={readOnly || inUse}
                    />
                </Grid>
                {/* TO */}
                <Grid item xs={12} md={3}>
                    <FormDateTimePicker
                        {...register("to")}
                        label={`${t("pricing:peaks.form.labels.endDateValidity")}*`}
                        minDate={watch("from")}
                        readOnly={readOnly}
                    />
                </Grid>
                <DealerAndTenant
                    readOnly={readOnly}
                    md={3}
                    legalEntityName={"legalEntity"}
                    dealerName={"dealer"}
                    dealerIsVisible={true}
                    idForReadOnly={getValues("id")}
                />
            </Grid>
            <Button ref={submitRef} sx={{display: "none"}} onClick={handleSubmit(confirmDefinition)}/>
        </Grid>
    );
};

export default PeakBaseDefinition;
